/**
 * New Application Page
 *
 * Multi-step form for creating a new halal certification application:
 * - Step 1: Application Type & Basic Info
 * - Step 2: Company & Facility Information
 * - Step 3: Document Upload
 * - Step 4: Review & Submit
 *
 * Features:
 * - Form validation
 * - Progress tracking
 * - Save as draft
 * - Step navigation
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '../../components/layouts/PageLayout';
import { Card, CardHeader, CardBody } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Badge } from '../../components/common/Badge';
import { Alert } from '../../components/common/Alert';
import { Input } from '../../components/forms/Input';
import { Select } from '../../components/forms/Select';
import { Textarea } from '../../components/forms/Textarea';
import { FileUpload } from '../../components/forms/FileUpload';
import {
  Wizard,
  WizardStep,
  WizardNavigation,
  WizardProgress,
  WizardFooter,
} from '../../components/wizard';
import { HelpPanel, HelpSection } from '../../components/help';
import { SuccessScreen } from '../../components/feedback/SuccessScreen';
import { FileText, Building2, Upload, CheckCircle } from 'lucide-react';
import type { ApplicationType } from '../../types/application';

interface ApplicationFormData {
  type: ApplicationType | '';
  previousCertificateNumber?: string;
  companyName: string;
  facilityAddress: {
    street: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
  };
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  products: Array<{
    name: string;
    description: string;
    category: string;
  }>;
  documents: File[];
}

const INITIAL_FORM_DATA: ApplicationFormData = {
  type: '',
  previousCertificateNumber: '',
  companyName: '',
  facilityAddress: {
    street: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
  },
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  products: [{ name: '', description: '', category: '' }],
  documents: [],
};

export const NewApplicationPage: React.FC = () => {
  const { t } = useTranslation(['common', 'company']);
  const navigate = useNavigate();
  const [formData, setFormData] = useState<ApplicationFormData>(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showSuccess, setShowSuccess] = useState(false);
  const [applicationNumber, setApplicationNumber] = useState('');

  const updateFormData = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateField = (field: string, value: any) => {
    let error = '';

    switch (field) {
      case 'type':
        if (!value) error = 'Application type is required';
        break;
      case 'companyName':
        if (!value || value.trim() === '') error = 'Company name is required';
        break;
      case 'contactName':
        if (!value || value.trim() === '') error = 'Contact name is required';
        break;
      case 'contactEmail':
        if (!value || value.trim() === '') {
          error = 'Contact email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          error = 'Please enter a valid email address';
        }
        break;
      case 'contactPhone':
        if (!value || value.trim() === '') {
          error = 'Contact phone is required';
        } else if (!/^[\d\s\-\+\(\)]+$/.test(value)) {
          error = 'Please enter a valid phone number';
        }
        break;
      case 'facilityAddress.street':
        if (!value || value.trim() === '') error = 'Street address is required';
        break;
      case 'facilityAddress.city':
        if (!value || value.trim() === '') error = 'City is required';
        break;
      case 'facilityAddress.country':
        if (!value || value.trim() === '') error = 'Country is required';
        break;
    }

    if (error) {
      setErrors((prev) => ({ ...prev, [field]: error }));
    } else {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const updateNestedFormData = (parent: string, field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [parent]: {
        ...(prev[parent as keyof ApplicationFormData] as any),
        [field]: value,
      },
    }));
  };

  const validateStep1 = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.type) {
      newErrors.type = 'Application type is required';
    }

    if (
      (formData.type === 'RENEWAL' || formData.type === 'MODIFICATION') &&
      !formData.previousCertificateNumber
    ) {
      newErrors.previousCertificateNumber = 'Previous certificate number is required for renewals/modifications';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.companyName) {
      newErrors.companyName = 'Company name is required';
    }
    if (!formData.facilityAddress.street) {
      newErrors['facilityAddress.street'] = 'Street address is required';
    }
    if (!formData.facilityAddress.city) {
      newErrors['facilityAddress.city'] = 'City is required';
    }
    if (!formData.facilityAddress.country) {
      newErrors['facilityAddress.country'] = 'Country is required';
    }
    if (!formData.contactName) {
      newErrors.contactName = 'Contact name is required';
    }
    if (!formData.contactEmail) {
      newErrors.contactEmail = 'Contact email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail)) {
      newErrors.contactEmail = 'Invalid email format';
    }
    if (!formData.contactPhone) {
      newErrors.contactPhone = 'Contact phone is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveDraft = async () => {
    // Save draft logic here
    console.log('Saving draft...', formData);
    alert('Application saved as draft (mock action)');
  };

  const handleSubmit = async () => {
    // Submit application logic here
    console.log('Submitting application...', formData);

    // Generate mock application number
    const appNumber = `APP-${new Date().getFullYear()}-${Math.floor(1000 + Math.random() * 9000)}`;
    setApplicationNumber(appNumber);
    setShowSuccess(true);
  };

  const addProduct = () => {
    setFormData((prev) => ({
      ...prev,
      products: [...prev.products, { name: '', description: '', category: '' }],
    }));
  };

  const updateProduct = (index: number, field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      products: prev.products.map((product, i) =>
        i === index ? { ...product, [field]: value } : product
      ),
    }));
  };

  const removeProduct = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      products: prev.products.filter((_, i) => i !== index),
    }));
  };

  const applicationTypeOptions = [
    { value: '', label: 'Select application type' },
    { value: 'NEW_CERTIFICATION', label: 'New Certification' },
    { value: 'RENEWAL', label: 'Renewal' },
    { value: 'MODIFICATION', label: 'Modification' },
    { value: 'RE_CERTIFICATION', label: 'Re-Certification' },
  ];

  const wizardSteps = [
    { id: 'type', label: 'Application Type', validate: validateStep1 },
    { id: 'company', label: 'Company Info', validate: validateStep2 },
    { id: 'documents', label: 'Documents' },
    { id: 'review', label: 'Review' },
  ];

  const renderStep1 = () => (
    <Card>
      <CardHeader>
        <h3 className="text-lg font-semibold">Step 1: Application Type & Basic Info</h3>
        <p className="text-sm text-gray-600 mt-1">Select the type of certification application</p>
      </CardHeader>
      <CardBody>
        <div className="space-y-6">
          <Select
            label="Application Type *"
            value={formData.type}
            options={applicationTypeOptions}
            onChange={(e) => updateFormData('type', e.target.value)}
            error={errors.type}
            required
          />

          {(formData.type === 'RENEWAL' || formData.type === 'MODIFICATION') && (
            <Input
              label="Previous Certificate Number *"
              value={formData.previousCertificateNumber || ''}
              onChange={(e) => updateFormData('previousCertificateNumber', e.target.value)}
              error={errors.previousCertificateNumber}
              placeholder="Enter your existing certificate number"
              required
            />
          )}

          {formData.type === 'NEW_CERTIFICATION' && (
            <Alert type="info">
              This is your first halal certification application. You'll need to provide comprehensive documentation
              about your facility, processes, and ingredients.
            </Alert>
          )}

          {formData.type === 'RENEWAL' && (
            <Alert type="info">
              Renewal applications typically process faster as we already have your base information. Please ensure
              all your documentation is current.
            </Alert>
          )}

          {formData.type && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h4 className="font-medium mb-2">What you'll need:</h4>
              <ul className="space-y-1 text-sm text-gray-700">
                <li>• Business registration documents</li>
                <li>• Facility layout and process flow diagrams</li>
                <li>• Ingredient lists with supplier information</li>
                <li>• Halal policy and training records</li>
                <li>• Supplier halal certificates</li>
              </ul>
            </div>
          )}
        </div>
      </CardBody>
    </Card>
  );

  const renderStep2 = () => (
    <Card>
      <CardHeader>
        <h3 className="text-lg font-semibold">Step 2: Company & Facility Information</h3>
        <p className="text-sm text-gray-600 mt-1">Provide details about your company and production facility</p>
      </CardHeader>
      <CardBody>
        <div className="space-y-6">
          <Input
            label="Company Name *"
            value={formData.companyName}
            onChange={(e) => updateFormData('companyName', e.target.value)}
            onBlur={(e) => validateField('companyName', e.target.value)}
            error={errors.companyName}
            required
          />

          <div>
            <h4 className="font-medium mb-3">Facility Address</h4>
            <div className="space-y-4">
              <Input
                label="Street Address *"
                value={formData.facilityAddress.street}
                onChange={(e) => updateNestedFormData('facilityAddress', 'street', e.target.value)}
                error={errors['facilityAddress.street']}
                required
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="City *"
                  value={formData.facilityAddress.city}
                  onChange={(e) => updateNestedFormData('facilityAddress', 'city', e.target.value)}
                  error={errors['facilityAddress.city']}
                  required
                />
                <Input
                  label="State/Province"
                  value={formData.facilityAddress.state}
                  onChange={(e) => updateNestedFormData('facilityAddress', 'state', e.target.value)}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Postal Code"
                  value={formData.facilityAddress.postalCode}
                  onChange={(e) => updateNestedFormData('facilityAddress', 'postalCode', e.target.value)}
                />
                <Input
                  label="Country *"
                  value={formData.facilityAddress.country}
                  onChange={(e) => updateNestedFormData('facilityAddress', 'country', e.target.value)}
                  error={errors['facilityAddress.country']}
                  required
                />
              </div>
            </div>
          </div>

          <div>
            <h4 className="font-medium mb-3">Contact Information</h4>
            <div className="space-y-4">
              <Input
                label="Contact Person Name *"
                value={formData.contactName}
                onChange={(e) => updateFormData('contactName', e.target.value)}
                onBlur={(e) => validateField('contactName', e.target.value)}
                error={errors.contactName}
                required
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  type="email"
                  label="Email Address *"
                  value={formData.contactEmail}
                  onChange={(e) => updateFormData('contactEmail', e.target.value)}
                  onBlur={(e) => validateField('contactEmail', e.target.value)}
                  error={errors.contactEmail}
                  placeholder="example@company.com"
                  required
                />
                <Input
                  type="tel"
                  label="Phone Number *"
                  value={formData.contactPhone}
                  onChange={(e) => updateFormData('contactPhone', e.target.value)}
                  onBlur={(e) => validateField('contactPhone', e.target.value)}
                  error={errors.contactPhone}
                  placeholder="+60 12-345-6789"
                  required
                />
              </div>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium">Products for Certification</h4>
              <Button size="sm" variant="outline" onClick={addProduct}>
                + Add Product
              </Button>
            </div>
            <div className="space-y-4">
              {formData.products.map((product, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-gray-700">Product {index + 1}</span>
                    {formData.products.length > 1 && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => removeProduct(index)}
                        className="text-red-600"
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                  <div className="space-y-3">
                    <Input
                      label="Product Name"
                      value={product.name}
                      onChange={(e) => updateProduct(index, 'name', e.target.value)}
                      placeholder="e.g., Chicken Nuggets"
                    />
                    <Input
                      label="Category"
                      value={product.category}
                      onChange={(e) => updateProduct(index, 'category', e.target.value)}
                      placeholder="e.g., Frozen Food"
                    />
                    <Textarea
                      label="Description"
                      value={product.description}
                      onChange={(e) => updateProduct(index, 'description', e.target.value)}
                      placeholder="Brief description of the product"
                      rows={2}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  );

  const renderStep3 = () => (
    <Card>
      <CardHeader>
        <h3 className="text-lg font-semibold">Step 3: Document Upload</h3>
        <p className="text-sm text-gray-600 mt-1">Upload required documents for your application</p>
      </CardHeader>
      <CardBody>
        <div className="space-y-6">
          <Alert type="info">
            You can upload documents now or add them later. However, your application won't be reviewed until all
            required documents are submitted.
          </Alert>

          <div>
            <h4 className="font-medium mb-3">Required Documents</h4>
            <div className="space-y-4">
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="mb-2">
                  <span className="font-medium text-sm">Business License</span>
                  <Badge variant="error" size="sm" className="ml-2">
                    Required
                  </Badge>
                </div>
                <FileUpload
                  onFileSelect={(files) => console.log('Business license:', files)}
                  accept=".pdf,.jpg,.jpeg,.png"
                  maxSize={10}
                />
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <div className="mb-2">
                  <span className="font-medium text-sm">Halal Policy Document</span>
                  <Badge variant="error" size="sm" className="ml-2">
                    Required
                  </Badge>
                </div>
                <FileUpload
                  onFileSelect={(files) => console.log('Halal policy:', files)}
                  accept=".pdf,.doc,.docx"
                  maxSize={10}
                />
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <div className="mb-2">
                  <span className="font-medium text-sm">Facility Layout / Floor Plan</span>
                  <Badge variant="error" size="sm" className="ml-2">
                    Required
                  </Badge>
                </div>
                <FileUpload
                  onFileSelect={(files) => console.log('Facility layout:', files)}
                  accept=".pdf,.jpg,.jpeg,.png,.dwg"
                  maxSize={20}
                />
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <div className="mb-2">
                  <span className="font-medium text-sm">Process Flow Diagram</span>
                  <Badge variant="warning" size="sm" className="ml-2">
                    Optional
                  </Badge>
                </div>
                <FileUpload
                  onFileSelect={(files) => console.log('Process flow:', files)}
                  accept=".pdf,.jpg,.jpeg,.png"
                  maxSize={10}
                />
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <div className="mb-2">
                  <span className="font-medium text-sm">Ingredient Lists & Supplier Certificates</span>
                  <Badge variant="error" size="sm" className="ml-2">
                    Required
                  </Badge>
                </div>
                <FileUpload
                  onFileSelect={(files) => console.log('Ingredients:', files)}
                  accept=".pdf,.xlsx,.xls"
                  maxSize={10}
                  multiple
                />
              </div>
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  );

  const renderStep4 = () => (
    <Card>
      <CardHeader>
        <h3 className="text-lg font-semibold">Step 4: Review & Submit</h3>
        <p className="text-sm text-gray-600 mt-1">Review your application before submission</p>
      </CardHeader>
      <CardBody>
        <div className="space-y-6">
          <Alert type="warning">
            Please review all information carefully. Once submitted, you won't be able to edit your application.
          </Alert>

          <div>
            <h4 className="font-medium mb-3">Application Summary</h4>
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">Application Type</dt>
                <dd className="mt-1 text-sm">
                  {applicationTypeOptions.find((opt) => opt.value === formData.type)?.label || 'Not selected'}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Company Name</dt>
                <dd className="mt-1 text-sm">{formData.companyName || 'Not provided'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Contact Person</dt>
                <dd className="mt-1 text-sm">{formData.contactName || 'Not provided'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Contact Email</dt>
                <dd className="mt-1 text-sm">{formData.contactEmail || 'Not provided'}</dd>
              </div>
              <div className="md:col-span-2">
                <dt className="text-sm font-medium text-gray-500">Facility Address</dt>
                <dd className="mt-1 text-sm">
                  {formData.facilityAddress.street || 'Not provided'}
                  {formData.facilityAddress.city && `, ${formData.facilityAddress.city}`}
                  {formData.facilityAddress.country && `, ${formData.facilityAddress.country}`}
                </dd>
              </div>
              <div className="md:col-span-2">
                <dt className="text-sm font-medium text-gray-500">Products</dt>
                <dd className="mt-1 text-sm">
                  {formData.products.filter((p) => p.name).length > 0
                    ? formData.products.filter((p) => p.name).map((p) => p.name).join(', ')
                    : 'No products added'}
                </dd>
              </div>
            </dl>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-blue-900 mb-2">Next Steps</h4>
            <ul className="space-y-1 text-sm text-blue-800">
              <li>• Your application will be reviewed by our certification team</li>
              <li>• A consultant will be assigned to guide you through the process</li>
              <li>• You'll be contacted within 3-5 business days</li>
              <li>• Pre-audit will be scheduled after initial review</li>
            </ul>
          </div>
        </div>
      </CardBody>
    </Card>
  );

  // Show success screen if application submitted
  if (showSuccess) {
    return (
      <PageLayout
        pageTitle="Application Submitted"
        breadcrumbs={[
          { label: t('common:navigation.home'), href: '/' },
          { label: t('company:dashboard.title'), href: '/company/dashboard' },
          { label: t('company:applications.title'), href: '/company/applications' },
          { label: 'Success' },
        ]}
      >
        <SuccessScreen
          title="Application Submitted Successfully!"
          message={`Your halal certification application has been submitted successfully. Your application number is ${applicationNumber}. Our team will review your application and contact you within 5-7 business days.`}
          primaryAction={{
            label: 'View My Applications',
            onClick: () => navigate('/company/applications'),
          }}
          secondaryAction={{
            label: 'Submit Another Application',
            onClick: () => {
              setShowSuccess(false);
              setFormData(INITIAL_FORM_DATA);
              setApplicationNumber('');
            },
          }}
          showAnimation={true}
        />
      </PageLayout>
    );
  }

  return (
    <PageLayout
      pageTitle="New Halal Certification Application"
      breadcrumbs={[
        { label: t('common:navigation.home'), href: '/' },
        { label: t('company:dashboard.title'), href: '/company/dashboard' },
        { label: t('company:applications.title'), href: '/company/applications' },
        { label: 'New Application' },
      ]}
    >
      <div className="flex gap-6">
        <div className="flex-1">
          <Wizard steps={wizardSteps} onComplete={handleSubmit} onSaveDraft={handleSaveDraft}>
            <div className="mb-6">
              <WizardProgress showPercentage showStepCount />
            </div>

            <div className="mb-6">
              <WizardNavigation />
            </div>

            <WizardStep>{renderStep1()}</WizardStep>
            <WizardStep>{renderStep2()}</WizardStep>
            <WizardStep>{renderStep3()}</WizardStep>
            <WizardStep>{renderStep4()}</WizardStep>

            <WizardFooter
              nextLabel="Next →"
              backLabel="← Previous"
              completeLabel="Submit Application"
              saveDraftLabel="Save as Draft"
              showSaveDraft
            />
          </Wizard>
        </div>

        <HelpPanel title="Application Guide" sticky>
          <HelpSection
            icon={FileText}
            title="Getting Started"
            description="Complete all steps to submit your halal certification application. You can save your progress and return later."
            variant="primary"
          />

          <HelpSection
            icon={Building2}
            title="Company Information"
            description="Ensure your company and facility details are accurate. This information will be used for all official documentation."
          />

          <HelpSection
            icon={Upload}
            title="Required Documents"
            description="Prepare these documents before starting:"
            collapsible
            defaultCollapsed
          >
            <ul className="list-disc list-inside space-y-1">
              <li>Business registration</li>
              <li>Facility layout plans</li>
              <li>Halal policy documentation</li>
              <li>Supplier certificates</li>
              <li>Ingredient specifications</li>
            </ul>
          </HelpSection>

          <HelpSection
            icon={CheckCircle}
            title="Processing Time"
            description="Applications are typically reviewed within 3-5 business days. You'll receive email updates at each stage."
            variant="success"
            cta={{
              label: 'View certification process',
              href: '#',
            }}
          />
        </HelpPanel>
      </div>
    </PageLayout>
  );
};

export default NewApplicationPage;
